import find from "lodash/find";
import map from "lodash/map";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { useTemplate } from "PFApp/hooks";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { isRankable } from "PFCore/helpers/custom_type";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useActivity, useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useCustomValue } from "PFCore/hooks/queries/custom_fields/use_custom_value";
import { useCustomValueActivityConnection } from "PFCore/hooks/queries/custom_fields/use_custom_value_activity_connection";
import { useCustomValuesCreate } from "PFCore/hooks/queries/custom_values/use_custom_values_create";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { AccessLevel, Activity, Experience, Importance } from "PFTypes";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCustomValuePreviewContext } from "../context/custom_value_preview_context";
import css from "../custom_value_preview.module.scss";
import { useProfileName } from "../hooks/use_profile_name";
import { CustomValueSelect } from "../parts/custom_value_select";
import { ActivityCustomValueFooter } from "../parts/footer/activity_custom_value_footer";
import { getLocalCustomValue } from "./activity_custom_value_preview.utils";
import { ActivityCustomValueContent } from "./parts/activity_custom_value_content";

export const ActivityCustomValuePreview = () => {
  const { t } = useTranslation("translation", { keyPrefix: "customValueSidepanel" });
  const { t: tActivities } = useTranslation("activities");

  const { data: currentProfile } = useCurrentProfile();
  const { hidePreview } = useCustomValuePreviewActions();
  const { getUnbiasedProfileName } = useUnbiasedProfileName();
  const {
    show,
    allowSearch,
    customType,
    activityId,
    staticActivity,
    staticActivityConnection,
    canEdit,
    isNew,
    isClosing,
    customValueId,
    index,
    key,
    profileId,
    isUnbiasedMode,
    setSelectedCustomValueId
  } = useCustomValuePreviewContext();

  const { profileName, isFetchingProfileName } = useProfileName();

  const { data: activityData, isFetching: isFetchingActivity } = useActivity(activityId!, {
    enabled: show && !staticActivity && !isClosing
  });

  const activity = staticActivity ?? activityData;

  const { values } = find(activity?.custom_fields || [], ["type.id", customType.id]) || { values: [] };
  const localCustomValue = getLocalCustomValue(values, customValueId);

  const { data: fetchedCustomValue, isFetching: isFetchingCustomValue } = useCustomValue(customValueId!, {
    enabled: Boolean(show && customValueId && !localCustomValue && !isClosing)
  });

  const customValue = localCustomValue ?? fetchedCustomValue;

  const connectionQueryEnabled = Boolean(
    show && customValueId && !isNew && !staticActivityConnection && !isClosing
  );

  const { data: activityConnectionData, isFetching: isFetchingActivityConnection } =
    useCustomValueActivityConnection(customValueId!, activityId!, {
      enabled: connectionQueryEnabled
    });

  const activityConnection = connectionQueryEnabled ? activityConnectionData : staticActivityConnection;

  const { mutateAsync: createCustomValue, isPending: isCreatingCustomValue } = useCustomValuesCreate();
  const { update: updateActivity, isUpdating } = useActivityUpdate(activity?.id);

  const template = useTemplate(activity?.template_id);

  const [importance, setImportance] = useState<Importance | null>(Importance.Essential);
  const [requiredExperience, setRequiredExperience] = useState<Experience | null>(Experience.Intermediate);

  const onResetForm = useCallback(() => {
    setSelectedCustomValueId(null);
    setImportance(activityConnection ? (activityConnection?.importance ?? null) : Importance.Essential);
    setRequiredExperience(
      activityConnection ? (activityConnection?.requiredExperience ?? null) : Experience.Intermediate
    );
  }, [activityConnection, setSelectedCustomValueId, setImportance, setRequiredExperience]);

  useEffect(() => {
    if (!activityConnection) {
      return;
    }
    setImportance(activityConnection?.importance ?? null);
    setRequiredExperience(activityConnection?.requiredExperience ?? null);
  }, [activityConnection]);

  useEffect(() => {
    onResetForm();
  }, [key, onResetForm]);

  useEffect(() => {
    if (allowSearch) {
      onResetForm();
    }
  }, [allowSearch, onResetForm]);

  const rankable = isRankable(customType);
  const isLoading =
    isFetchingActivity ||
    isFetchingCustomValue ||
    isFetchingActivityConnection ||
    isCreatingCustomValue ||
    (!isUnbiasedMode && isFetchingProfileName);
  const isAllDataFetched =
    (!!activity || !!staticActivity) &&
    !!customValue &&
    (isNew || !!activityConnection || !!staticActivityConnection);

  const title = isNew && !customValue ? t("addCustomValue") : customValue?.text;
  const showSubtitle = !!title && (!isNew || !!customValue);
  const subtitle =
    isUnbiasedMode && profileId
      ? getUnbiasedProfileName({ id: Number(profileId) }, true)
      : (profileName ?? customType?.display_as);
  const showCustomValueSelect = !isLoading && isNew && !customValue && allowSearch;
  const showContent = !isLoading && isAllDataFetched;
  const allowEdit =
    !!canEdit &&
    !!activity &&
    activityCustomTypesAccessLevels(currentProfile, activity.profile)[customType.id] ===
      AccessLevel.ReadWrite;
  const isFormFilled = importance !== null && (!rankable || requiredExperience !== null);
  const templateName = template
    ? tActivities(`templates.${template?.key}` as unknown as TemplateStringsArray)
    : null;
  const customValuesIdsBlacklist = map(values, "id");

  const renderFooter = useCallback(
    ({ onSidePanelClose }) => (
      <ActivityCustomValueFooter
        activity={activity as Activity}
        customValue={customValue}
        importance={importance}
        requiredExperience={requiredExperience}
        allowEdit={allowEdit}
        isRemoveDisabled={isLoading}
        isSaveDisabled={isLoading || !isAllDataFetched || !isFormFilled}
        isSaveTooltipVisible={isAllDataFetched && !isFormFilled}
        updateActivity={updateActivity}
        onClose={onSidePanelClose}
      />
    ),
    [
      activity,
      allowEdit,
      customValue,
      isAllDataFetched,
      importance,
      isFormFilled,
      isLoading,
      requiredExperience,
      updateActivity
    ]
  );

  return (
    <SidePanel
      fullHeight
      show={show}
      title={title}
      subTitle={showSubtitle ? subtitle : undefined}
      isSidePanelClosing={isClosing}
      footerRenderer={renderFooter}
      onClose={hidePreview}
      zIndex={index}
    >
      {!!isUpdating && (
        <div className={css.savingOverlay}>
          <LoadingDots circlesEnabled circleSize="xl" />
        </div>
      )}
      <div className={css.sidePanelRoot}>
        {isLoading && <LoadingDots centered circlesEnabled circleSize="sm" />}
        {showCustomValueSelect && (
          <CustomValueSelect
            customType={customType}
            customValuesIdsBlacklist={customValuesIdsBlacklist}
            createCustomValue={createCustomValue}
          />
        )}
        {showContent && (
          <ActivityCustomValueContent
            importance={importance}
            requiredExperience={requiredExperience}
            allowEdit={allowEdit}
            customValue={customValue}
            templateName={templateName}
            onRequiredExperienceChange={setRequiredExperience}
            onImportanceChange={setImportance}
            onResetForm={onResetForm}
          />
        )}
      </div>
    </SidePanel>
  );
};
